import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Card, CardContent,
  List, ListItem, ListItemText, Switch, Divider,
  IconButton, Alert, Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';

// Default API URL from environment or fallback
const DEFAULT_API_URL = process.env.REACT_APP_API_URL || 'https://pacehere.com';

const Settings = () => {
  // State for managing endpoints
  const [endpoints, setEndpoints] = useState([
    { id: 'default', name: 'Default', url: DEFAULT_API_URL, isDefault: true }
  ]);
  const [activeEndpointId, setActiveEndpointId] = useState('default');
  
  // State for the new endpoint form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEndpoint, setNewEndpoint] = useState({ name: '', url: '' });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Save settings to localStorage
  const saveSettings = (newEndpoints, newActiveId) => {
    const settingsToSave = {
      endpoints: newEndpoints,
      activeEndpointId: newActiveId
    };
    
    localStorage.setItem('api_settings', JSON.stringify(settingsToSave));
    
    // Also set the active URL in a separate key for backward compatibility
    const activeEndpoint = newEndpoints.find(e => e.id === newActiveId);
    if (activeEndpoint) {
      localStorage.setItem('api_base_url', activeEndpoint.url);
    }
  };

  // Wrap resetToDefault in useCallback to avoid recreating it on every render
  const resetToDefault = useCallback(() => {
    const defaultEndpoints = [
      { id: 'default', name: 'Default', url: DEFAULT_API_URL, isDefault: true }
    ];
    setEndpoints(defaultEndpoints);
    setActiveEndpointId('default');
    saveSettings(defaultEndpoints, 'default');
  }, []);  // Empty dependency array since it doesn't depend on changing values

  // Load saved settings when component mounts
  useEffect(() => {
    const savedSettings = localStorage.getItem('api_settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        
        // Always ensure the default endpoint exists
        let defaultExists = false;
        const loadedEndpoints = parsedSettings.endpoints || [];
        
        for (const endpoint of loadedEndpoints) {
          if (endpoint.isDefault) {
            defaultExists = true;
            break;
          }
        }
        
        if (!defaultExists) {
          loadedEndpoints.push({ 
            id: 'default', 
            name: 'Default', 
            url: DEFAULT_API_URL, 
            isDefault: true 
          });
        }
        
        setEndpoints(loadedEndpoints);
        setActiveEndpointId(parsedSettings.activeEndpointId || 'default');
      } catch (error) {
        console.error('Error parsing saved API settings:', error);
        // If parsing fails, reset to default
        resetToDefault();
      }
    }
  }, [resetToDefault]); // Now resetToDefault is properly memoized

  const handleAddEndpoint = () => {
    // Basic validation
    if (!newEndpoint.name || !newEndpoint.url) {
      setSnackbar({
        open: true,
        message: 'Name and URL are required',
        severity: 'error'
      });
      return;
    }
    
    try {
      // Validate URL format
      new URL(newEndpoint.url);
      
      // Create new endpoint with unique ID
      const newId = `endpoint_${Date.now()}`;
      const updatedEndpoints = [
        ...endpoints,
        { 
          id: newId, 
          name: newEndpoint.name, 
          url: newEndpoint.url, 
          isDefault: false 
        }
      ];
      
      // Update state and save
      setEndpoints(updatedEndpoints);
      saveSettings(updatedEndpoints, activeEndpointId);
      
      // Reset form
      setNewEndpoint({ name: '', url: '' });
      setShowAddForm(false);
      
      setSnackbar({
        open: true,
        message: 'New endpoint added successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid URL (including http:// or https://)',
        severity: 'error'
      });
    }
  };

  const handleRemoveEndpoint = (id) => {
    // Don't allow removing the default endpoint
    const endpointToRemove = endpoints.find(e => e.id === id);
    if (endpointToRemove?.isDefault) {
      setSnackbar({
        open: true,
        message: 'Cannot remove the default endpoint',
        severity: 'error'
      });
      return;
    }
    
    // Remove the endpoint
    const updatedEndpoints = endpoints.filter(e => e.id !== id);
    setEndpoints(updatedEndpoints);
    
    // If the active endpoint was removed, switch to default
    if (activeEndpointId === id) {
      const defaultEndpoint = updatedEndpoints.find(e => e.isDefault);
      setActiveEndpointId(defaultEndpoint.id);
      saveSettings(updatedEndpoints, defaultEndpoint.id);
    } else {
      saveSettings(updatedEndpoints, activeEndpointId);
    }
    
    setSnackbar({
      open: true,
      message: 'Endpoint removed successfully',
      severity: 'success'
    });
  };

  const handleActivateEndpoint = (id) => {
    setActiveEndpointId(id);
    saveSettings(endpoints, id);
    
    setSnackbar({
      open: true,
      message: 'API endpoint changed. Please reload the application for changes to take effect.',
      severity: 'success'
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <SettingsIcon sx={{ mr: 1 }} />
        <Typography variant="h5" component="h1">
          Application Settings
        </Typography>
      </Box>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          API Configuration
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              API Endpoints
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure API endpoints for different environments. The active endpoint will be used for all API requests.
            </Typography>
            
            <List>
              {endpoints.map((endpoint) => (
                <ListItem 
                  key={endpoint.id}
                  secondaryAction={
                    !endpoint.isDefault && (
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleRemoveEndpoint(endpoint.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                  divider
                >
                  <Switch
                    checked={activeEndpointId === endpoint.id}
                    onChange={() => handleActivateEndpoint(endpoint.id)}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {endpoint.name} {endpoint.isDefault && "(Default)"}
                      </Typography>
                    }
                    secondary={endpoint.url}
                  />
                </ListItem>
              ))}
            </List>
            
            {showAddForm ? (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Add New Endpoint</Typography>
                <TextField
                  label="Name"
                  size="small"
                  fullWidth
                  value={newEndpoint.name}
                  onChange={(e) => setNewEndpoint({...newEndpoint, name: e.target.value})}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="URL"
                  size="small"
                  fullWidth
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint({...newEndpoint, url: e.target.value})}
                  placeholder="https://api.example.com"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setShowAddForm(false);
                      setNewEndpoint({ name: '', url: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<SaveIcon />}
                    onClick={handleAddEndpoint}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                sx={{ mt: 2 }}
              >
                Add New Endpoint
              </Button>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Button
              variant="outlined"
              color="error"
              onClick={resetToDefault}
            >
              Reset to Default
            </Button>
          </CardContent>
        </Card>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
